import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { motion } from "framer-motion";
import {
	CalendarDays,
	Clock,
	HelpCircle,
	MapPin,
	PartyPopper,
	Users,
	XCircle,
} from "lucide-react";
import { ErrorComponent } from "~/components/error";
import { Button } from "~/components/ui/button";
import { eventApi } from "~/lib/api/event";
import { userApi } from "~/lib/api/user";
import { cn } from "~/lib/utils";

export const Route = createFileRoute("/invite/$eventSlug")({
	beforeLoad: async ({ params, location }) => {
		const user = await userApi.getPartialUser().catch(() => null);
		if (user) {
			return redirect({
				to: `/event/${params.eventSlug}`,
				search: location.search,
			});
		}
	},
	loader: async ({ params }) => {
		const response = await eventApi.getEventInvite(params.eventSlug);
		if (!response.success) {
			throw new Error(response.message);
		}
		const { rsvpCount, ...event } = response.data;
		return { event, rsvpCount };
	},
	component: InviteComponent,
	errorComponent: ErrorComponent,
});

function InviteComponent() {
	const { event } = Route.useLoaderData();
	const navigate = useNavigate();

	// Format date and time
	const startDate = new Date(event.startDate);
	const hasEventPassed = startDate.getTime() < Date.now();
	const daysUntil = Math.ceil(
		(startDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24),
	);

	const formattedDate = new Intl.DateTimeFormat("en-US", {
		weekday: "long",
		month: "long",
		day: "numeric",
		year: "numeric",
	}).format(startDate);

	const formattedTime = new Intl.DateTimeFormat("en-US", {
		hour: "numeric",
		minute: "2-digit",
		hour12: true,
	}).format(startDate);

	const container = {
		hidden: { opacity: 0 },
		show: {
			opacity: 1,
			transition: {
				staggerChildren: 0.1,
				duration: 0.2,
			},
		},
	};

	const item = {
		hidden: { opacity: 0, y: 10 },
		show: { opacity: 1, y: 0, transition: { duration: 0.2 } },
	};

	const handleRsvp = (choice: "accepted" | "maybe" | "declined") => {
		navigate({
			to: "/login",
			search: { redirect: `/invite/${event.slug}`, choice },
		});
	};

	return (
		<div className="flex flex-col min-h-screen bg-black text-primary-foreground">
			{/* Event Status Banner */}
			{hasEventPassed && (
				<motion.div
					initial={{ y: -100 }}
					animate={{ y: 0 }}
					transition={{ type: "spring", bounce: 0.2 }}
					className="sticky top-0 left-0 right-0 z-40 bg-primary/10 backdrop-blur-xl border-b border-white/10 py-2"
				>
					<div className="max-w-3xl mx-auto px-4">
						<div className="flex items-center justify-center gap-2 text-sm font-medium text-chart-1/50">
							<Clock className="w-4 h-4" strokeWidth={2.5} />
							<span>This event has already taken place</span>
						</div>
					</div>
				</motion.div>
			)}

			{/* Content Area */}
			<div className="flex-1 overflow-auto pb-0">
				{/* Background */}
				<div className="fixed inset-0 bg-gradient-to-b from-black from-85% to-primary/5" />

				{/* Scrollable Content */}
				<div className="relative z-10 max-w-3xl mx-auto px-4 py-8 md:py-12 mb-16">
					{/* Header Section with Image */}
					<div className="md:grid md:grid-cols-2 md:gap-8 mb-6">
						{/* Event Image */}
						{event.coverImage && (
							<motion.div
								variants={item}
								className="w-3/4 md:w-full mx-auto aspect-square overflow-hidden rounded-2xl border border-white/10 bg-primary/10 mb-6 md:mb-0"
							>
								<motion.img
									src={event.coverImage}
									alt={`${event.title} cover`}
									className="w-full h-full object-cover"
									whileHover={{ scale: 1.05 }}
									whileTap={{ scale: 1.02 }}
									transition={{ duration: 0.3, ease: "easeOut" }}
								/>
							</motion.div>
						)}

						{/* Title Section */}
						<motion.div
							variants={container}
							initial="hidden"
							animate="show"
							className={cn(
								"text-center md:text-left flex flex-col justify-center",
								!event.coverImage &&
									"md:col-span-2 md:max-w-2xl md:mx-auto md:py-4",
							)}
						>
							{/* Event Badge */}
							<motion.div variants={item}>
								<div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 backdrop-blur-sm border border-white/10 mb-3">
									<PartyPopper className="w-4 h-4 text-accent" />
									<span className="text-sm font-medium text-primary-foreground">
										{hasEventPassed
											? "Event has ended"
											: daysUntil > 0
												? `${daysUntil} days to go!`
												: "Event starts today!"}
									</span>
								</div>
							</motion.div>

							{/* Title */}
							<motion.h1
								variants={item}
								className={cn(
									"font-bold mb-2 text-primary-foreground font-heading tracking-wide",
									event.coverImage
										? "text-4xl md:text-5xl"
										: "text-6xl md:text-8xl bg-gradient-to-br from-white via-white/90 to-primary-foreground/80 bg-clip-text text-transparent",
								)}
							>
								{event.title}
							</motion.h1>
							<motion.p
								variants={item}
								className={cn(
									"text-secondary",
									event.coverImage ? "text-lg" : "text-2xl tracking-wide",
								)}
							>
								Hosted by{" "}
								<span className="text-accent font-medium">
									{event.hostName}
								</span>
							</motion.p>
						</motion.div>
					</div>

					{/* Details Card */}
					<motion.div
						variants={container}
						initial="hidden"
						animate="show"
						className={cn(
							"space-y-6 backdrop-blur-xl bg-primary/10 rounded-3xl border border-white/10 p-6 md:p-8",
							!event.coverImage && "mt-8",
						)}
					>
						{/* Event Details */}
						<div className="space-y-6">
							{/* Date and Time */}
							<motion.div variants={item} className="flex items-start gap-4">
								<div className="shrink-0 p-3 rounded-xl bg-accent/10 border border-accent/20">
									<CalendarDays className="w-6 h-6 text-accent" />
								</div>
								<div>
									<h2 className="font-medium mb-1 text-primary-foreground">
										Date
									</h2>
									<p className="text-secondary">{formattedDate}</p>
								</div>
							</motion.div>

							<motion.div variants={item} className="flex items-start gap-4">
								<div className="shrink-0 p-3 rounded-xl bg-accent/10 border border-accent/20">
									<Clock className="w-6 h-6 text-accent" />
								</div>
								<div>
									<h2 className="font-medium mb-1 text-primary-foreground">
										Time
									</h2>
									<p className="text-secondary">{formattedTime}</p>
								</div>
							</motion.div>

							{/* Location - New Section */}
							<motion.div variants={item} className="flex items-start gap-4">
								<div className="shrink-0 p-3 rounded-xl bg-accent/10 border border-accent/20">
									<MapPin className="w-6 h-6 text-accent" />
								</div>
								<div>
									<h2 className="font-medium mb-1 text-primary-foreground">
										Location
									</h2>
									<p className="text-secondary">RSVP to see location</p>
								</div>
							</motion.div>

							{/* Guest Count - Modified to handle visibility */}
							<motion.div variants={item} className="flex items-start gap-4">
								<div className="shrink-0 p-3 rounded-xl bg-accent/10 border border-accent/20">
									<Users className="w-6 h-6 text-accent" />
								</div>
								<div>
									<h2 className="font-medium mb-1 text-primary-foreground">
										Guests
									</h2>
									<p className="text-secondary flex items-center flex-wrap space-x-3">
										<span>RSVP to see guest list</span>
										{event.guestLimit && (
											<span className="px-2 py-0.5 rounded-full bg-accent-dark text-xs text-white">
												{event.guestLimit} max
											</span>
										)}
										{event.allowPlusOne && (
											<span className="px-2 py-0.5 rounded-full bg-accent/10 text-accent text-sm">
												Plus ones welcome
											</span>
										)}
									</p>
								</div>
							</motion.div>

							{/* Description */}
							{event.description && (
								<motion.div
									variants={item}
									className="mt-8 p-6 rounded-2xl bg-primary/10 border border-white/10 mb-6"
								>
									<p className="text-secondary whitespace-pre-wrap leading-relaxed">
										{event.description}
									</p>
								</motion.div>
							)}
						</div>
					</motion.div>
				</div>
			</div>

			{/* Sign In Button */}
			{!hasEventPassed && (
				// <div className="sticky bottom-0 bg-black/90 backdrop-blur-xl border-t border-white/10 z-10">
				// 	<div className="max-w-3xl mx-auto px-4 py-4">
				// 		<Link
				// 			to="/login"
				// 			search={{ redirect: `/invite/${event.slug}` }}
				// 			className={cn(
				// 				"flex items-center justify-center gap-2 w-full h-14 px-6 rounded-2xl",
				// 				"bg-accent text-accent-foreground hover:bg-accent/90",
				// 				"transition-all duration-300",
				// 				"font-medium",
				// 			)}
				// 		>
				// 			<PartyPopper className="w-5 h-5" />
				// 			RSVP
				// 		</Link>
				// 	</div>
				// </div>
				<motion.div
					initial={{ y: 100 }}
					animate={{ y: 0 }}
					transition={{ type: "spring", bounce: 0.2 }}
					className="fixed bottom-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-xl border-t border-white/10 pb-safe"
				>
					<div className="grid grid-cols-3 gap-4 max-w-3xl mx-auto p-4">
						<Button
							size="lg"
							className={cn(
								"py-6 rounded-2xl font-semibold text-base",
								"bg-white/5 hover:bg-accent active:bg-accent-dark",
								"text-white/70 hover:text-accent-foreground",
								"transition-all duration-300",
								"border border-white/10 hover:border-accent",
								"shadow-[0_0_0_0] hover:shadow-[0_0_32px_rgba(111,136,247,0.3)]",
							)}
							onClick={() => handleRsvp("accepted")}
						>
							<span className="flex items-center gap-2">
								<PartyPopper className="w-5 h-5" strokeWidth={2.5} />
								<span>Going!</span>
							</span>
						</Button>
						<Button
							size="lg"
							className={cn(
								"py-6 rounded-2xl font-semibold text-base",
								"bg-white/5 hover:bg-amber-500 active:bg-amber-500",
								"text-white/70 hover:text-white/70",
								"transition-all duration-300",
								"border border-white/10 hover:border-amber-600",
								"shadow-[0_0_0_0] hover:shadow-[0_0_32px_rgba(255,165,0,0.3)]",
							)}
							onClick={() => handleRsvp("maybe")}
						>
							<span className="flex items-center gap-2">
								<HelpCircle className="w-5 h-5" strokeWidth={2.5} />
								<span>Maybe</span>
							</span>
						</Button>
						<Button
							size="lg"
							className={cn(
								"py-6 rounded-2xl font-semibold text-base",
								"bg-white/5 hover:bg-destructive active:bg-destructive-dark",
								"text-white/70 hover:text-white",
								"transition-all duration-300",
								"border border-white/10 hover:border-destructive",
								"shadow-[0_0_0_0] hover:shadow-[0_0_32px_rgba(238,57,57,0.3)]",
							)}
							onClick={() => handleRsvp("declined")}
						>
							<span className="flex items-center gap-2">
								<XCircle className="w-5 h-5" strokeWidth={2.5} />
								<span>No</span>
							</span>
						</Button>
					</div>
				</motion.div>
			)}
		</div>
	);
}
